import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import moment from 'moment-timezone'
import { CronTimeZones } from "@/constants/cron/cron"

dayjs.extend(utc)
dayjs.extend(timezone)

export const createDateString = (
  executionStartDate: string,
  hour: number,
  minute: number,
  amPm: string,
) => {
  const adjustedHour = amPm === 'PM' && hour < 12 ? hour + 12 : hour
  const date = new Date(executionStartDate)
  date.setHours(adjustedHour)
  date.setMinutes(minute)
  const dateString = date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

  return {
    displayValue: dateString,
    value: dayjs(dateString).format('YYYY-MM-DDTHH:mm:ss') + '.000Z',
  }
}

export const parseISOToDateParts = (isoString: string) => {
  const hour = (new Date(isoString)).getUTCHours()
  return {
    date: isoString,
    hour: hour >= 12 ? hour - 12 : hour,
    minute: new Date(isoString).getUTCMinutes(),
    amPm: hour >= 12 ? 'PM' : 'AM',
  }
}

export const formatISOStringToDate = (isoString: string | undefined) => {
  if (!isoString) return ''
  const date = new Date(isoString)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  }).format(date)
}

export const convertQuartzCronToUTC = (
  quartzCron: string,
  timezone: string
): string => {
  const parts = quartzCron.split(' ')

  if (parts.length !== 7) {
    throw new Error('Invalid Quartz cron expression. It must have 7 fields.')
  }

  // eslint-disable-next-line prefer-const
  let [sec, min, hour, dayOfMonth, month, dayOfWeek, year] = parts

  /**
   * Converts a numeric time field (hour/minute) to UTC.
   * If the field contains special characters (*, /, -, etc.), return as is.
   */
  const convertTimeField = (field: string, unit: 'hour' | 'minute'): string => {
    if (/^[0-9]+$/.test(field)) {
      // Use current date for accurate timezone offset (handles DST correctly)
      const now = moment().tz(CronTimeZones[timezone]).set({
        second: parseInt(sec, 10),
        minute: unit === 'minute' ? parseInt(field, 10) : 0,
        hour: unit === 'hour' ? parseInt(field, 10) : 0
      })
      return String(unit === 'hour' ? now.utc().hour() : now.utc().minute())
    }
    return field // Return unchanged if it's *, -, /, ?, L, W, #
  }

  // Convert only numeric values in hour and minute fields
  hour = convertTimeField(hour, 'hour')
  min = convertTimeField(min, 'minute')

  // Reconstruct the UTC Quartz cron expression
  return [sec, min, hour, dayOfMonth, month, dayOfWeek, year].join(' ')
}

export const convertUTCToQuartzCron = (utcCron: string, timezone: string): string => {
  const parts = utcCron.split(' ')

  if (parts.length !== 7) {
    throw new Error('Invalid Quartz cron expression. It must have 7 fields.')
  }

  // eslint-disable-next-line prefer-const
  let [sec, min, hour, dayOfMonth, month, dayOfWeek, year] = parts

  /**
   * Converts a numeric time field (hour/minute) from UTC to the target timezone.
   * If the field contains special characters (*, /, -, etc.), return as is.
   */
  const convertTimeField = (field: string, unit: 'hour' | 'minute'): string => {
    if (/^[0-9]+$/.test(field)) {
      // Use current date for accurate timezone offset (handles DST correctly)
      const now = moment.utc().set({
        second: parseInt(sec, 10),
        minute: unit === 'minute' ? parseInt(field, 10) : 0,
        hour: unit === 'hour' ? parseInt(field, 10) : 0
      }).tz(CronTimeZones[timezone])
      return String(unit === 'hour' ? now.hour() : now.minute())
    }
    return field // Keep * / - , L W # as they are
  }

  // Convert only numeric values in hour and minute fields
  hour = convertTimeField(hour, 'hour')
  min = convertTimeField(min, 'minute')

  // Reconstruct the timezone-specific Quartz cron expression
  return [sec, min, hour, dayOfMonth, month, dayOfWeek, year].join(' ')
}

export const convertToUTC = (
  dateString: string,
  hour: number,
  minute: number,
  period: string,
  timezone: string
): string => {
  // Convert 12-hour format to 24-hour format
  const hour24 = period === 'PM' && hour !== 12 ? hour + 12 : hour === 12 && period === 'AM' ? 0 : hour

  // Extract just the date part
  const datePart = moment.tz(dateString, CronTimeZones[timezone]).format('YYYY-MM-DD')

  // Combine with new hour and minute
  const newDateTimeString = `${datePart} ${hour24}:${minute}`

  // Convert to UTC
  return moment.tz(newDateTimeString, 'YYYY-MM-DD HH:mm', CronTimeZones[timezone])
    .utc()
    .format('YYYY-MM-DDTHH:mm:ss.000[Z]')
}

export const convertFromUTC = (
  dateString: string, // UTC Date String
  timezone: string
): { dateString: string, hour: number, minute: number, period: string } => {
  // Convert UTC date to the specified timezone
  const localTime = moment.utc(dateString).tz(CronTimeZones[timezone])

  // Convert 24-hour format to 12-hour with AM/PM
  const hour24 = localTime.hour()
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12

  return {
    dateString: localTime.format('YYYY-MM-DD'), // Use local date format
    hour: hour12,
    minute: localTime.minute(),
    period // Explicitly cast to AM/PM
  }
}

export const formatISOStringToTimeZone = (
  isoString: string | undefined,
  timezone: string = "America/Los_Angeles" // PST/PDT
): string => {
  if (!isoString) return ""
  return moment.tz(isoString, timezone).format("YYYY-MM-DD hh:mm A")
}

/**
 * Encodes original cron and timezone as JSON metadata for storage in scheduleType field.
 * This ensures WYSIWYG (What You See Is What You Get) - users always see what they submitted.
 *
 * @param cron - Original Quartz cron expression in user's timezone
 * @param timezone - User-selected timezone (PST/EST/IST)
 * @returns JSON string containing original values
 */
export const encodeCronMetadata = (cron: string, timezone: string): string => {
  return JSON.stringify({ originalCron: cron, originalTimezone: timezone })
}

/**
 * Decodes cron metadata from scheduleType field.
 * Returns null if metadata doesn't exist or is invalid (e.g., old workflows).
 *
 * @param scheduleType - The scheduleType field value from backend
 * @returns Decoded metadata or null for fallback to UTC conversion
 */
export const decodeCronMetadata = (
  scheduleType: string
): { originalCron: string; originalTimezone: string } | null => {
  if (!scheduleType || scheduleType.trim() === '') {
    return null
  }

  try {
    const parsed = JSON.parse(scheduleType)
    if (parsed.originalCron && parsed.originalTimezone) {
      return {
        originalCron: parsed.originalCron,
        originalTimezone: parsed.originalTimezone,
      }
    }
    return null
  } catch {
    // Old workflows with non-JSON scheduleType values
    return null
  }
}
